import { useState, useEffect, useCallback } from "react";
import { checkIfPdf, rotateImageDataUrl } from "../utils";

/**
 * Detects whether the given URL is a PDF (async).
 * @param {string} image - Data URL or blob URL
 * @returns {{ isPdf: boolean, isCheckingType: boolean }}
 */
export function useImageType(image) {
  const [isPdf, setIsPdf] = useState(false);
  const [isCheckingType, setIsCheckingType] = useState(true);

  useEffect(() => {
    if (!image) {
      setIsPdf(false);
      setIsCheckingType(false);
      return;
    }
    let cancelled = false;
    setIsCheckingType(true);
    checkIfPdf(image).then((pdf) => {
      if (!cancelled) {
        setIsPdf(pdf);
        setIsCheckingType(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [image]);

  return { isPdf, isCheckingType };
}

/**
 * Manages rotation state and display image for the editor.
 * Rotates from original each time to avoid compounding quality loss;
 * final output should use single-pass rotate+crop in getCroppedImage.
 */
export function useImageEditorRotation(originalImage, isPdf) {
  const [displayImage, setDisplayImage] = useState(null);
  const [rotationDegrees, setRotationDegrees] = useState(0);
  const [isRotating, setIsRotating] = useState(false);

  useEffect(() => {
    if (!originalImage) {
      setDisplayImage(null);
      setRotationDegrees(0);
      return;
    }
    setDisplayImage(originalImage);
    setRotationDegrees(0);
  }, [originalImage]);

  const handleRotate = useCallback(() => {
    if (!originalImage || isPdf) return;
    setIsRotating(true);
    const next = (rotationDegrees + 90) % 360;
    setRotationDegrees(next);
    if (next === 0) {
      setDisplayImage(originalImage);
      setIsRotating(false);
      return;
    }
    rotateImageDataUrl(originalImage, next)
      .then(setDisplayImage)
      .catch(() => {})
      .finally(() => setIsRotating(false));
  }, [originalImage, isPdf, rotationDegrees]);

  return { displayImage, rotationDegrees, isRotating, handleRotate };
}
